/**
 * The Blender geometry library.
 *
 * `public/models/pieces.glb` holds a flat set of named meshes generated by
 * `blender/generate_pieces.py`. Materials, the rig and every animation stay in
 * JavaScript — only the shapes come from the file, so one library serves both
 * armies and the animator never has to know a model changed.
 *
 * Parts come in two flavours, matching the generator:
 *
 *   canonical  unit-sized (`box`, `limb`, `cylinder`, ...). Ask for one at the
 *              size you want and the scale is baked into a cached clone.
 *   bespoke    already at final size (`head`, `shield`, `horseHead`, ...).
 *
 * If the file is missing the library simply stays empty and `pieces.js` falls
 * back to the primitives it used before, so the game still boots.
 *
 * A second, separate path loads *rigs*: models whose node hierarchy matters,
 * like the sculpted knight in `knight.glb`. The library path above deliberately
 * throws that hierarchy away, so the two cannot share an implementation.
 */

import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

const library = new Map();
const scaled = new Map();
const rigs = new Map();
let ready = false;

/** Loads the library. Safe to call twice; the second call is a no-op. */
export async function loadParts(url) {
  if (ready) return true;
  try {
    const gltf = await new GLTFLoader().loadAsync(url);
    gltf.scene.traverse((o) => {
      if (!o.isMesh) return;
      const g = o.geometry;
      // The GLB carries no materials and no UVs; drop anything else the
      // exporter may have attached so the buffers stay small.
      for (const attr of Object.keys(g.attributes)) {
        if (attr !== 'position' && attr !== 'normal') g.deleteAttribute(attr);
      }
      library.set(o.name, g);
    });
    ready = library.size > 0;
  } catch (err) {
    console.warn('[parts] geometry library unavailable, using primitives', err);
    ready = false;
  }
  return ready;
}

export const partsReady = () => ready;

/** A bespoke part at its authored size, or null if the library is absent. */
export function part(name) {
  return library.get(name) ?? null;
}

/**
 * A canonical part baked to the given size.
 *
 * The scale is applied to a cached clone rather than to `mesh.scale`, because
 * callers routinely set `mesh.scale` themselves afterwards and would otherwise
 * wipe the sizing out.
 */
export function partSized(name, sx, sy, sz) {
  const base = library.get(name);
  if (!base) return null;
  const key = `${name}|${sx}|${sy}|${sz}`;
  let g = scaled.get(key);
  if (!g) {
    g = base.clone().applyMatrix4(new THREE.Matrix4().makeScale(sx, sy, sz));
    g.computeBoundingSphere();
    scaled.set(key, g);
  }
  return g;
}

/**
 * Loads a model whose *hierarchy* is the point.
 *
 * `loadParts` above keeps only geometry, flattens the tree and deletes every
 * attribute but position and normal — correct for a library of interchangeable
 * shapes, and load-bearing for the shared-geometry rule in debris.js. A rig is
 * the opposite: the named nodes and their pivots are the whole value, because
 * `userData.parts.armR` in pieces.js *is* a node in the file.
 *
 * Failure is non-fatal, exactly as with the library: `rig()` returns null and
 * the caller builds the piece the way it did before.
 */
export async function loadRig(key, url) {
  if (rigs.has(key)) return true;
  try {
    const gltf = await new GLTFLoader().loadAsync(url);
    rigs.set(key, gltf.scene);
  } catch (err) {
    console.warn(`[parts] rig "${key}" unavailable, using primitives`, err);
    return false;
  }
  return true;
}

export const rigReady = (key) => rigs.has(key);

/**
 * A fresh copy of a rig, and a lookup of its named nodes.
 *
 * Cloned per piece rather than shared: four knights sit on the board at once
 * and each poses independently. Object3D.clone shares geometry between copies,
 * so a rig mesh is under the same rule as a library part — debris.js must never
 * dispose one, and disposeParts frees each rig's geometry exactly once.
 */
export function rig(key) {
  const source = rigs.get(key);
  if (!source) return null;
  const root = source.clone(true);
  const nodes = new Map();
  root.traverse((o) => {
    // `root` itself is GLTFLoader's own scene wrapper (auto-named "Scene"),
    // not a node the importer named — the real hierarchy starts at its
    // children. Included, it shows up as a fake extra "part" with no meaning:
    // moving it moves the whole piece, and it is never what
    // userData.parts.name is meant to address.
    if (o === root) return;
    nodes.set(o.name, o);
    if (o.isMesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  return { root, nodes };
}

/**
 * One named mesh out of a loaded rig.
 *
 * `rig()` above hands back a whole cloned tree, which is what a figure wants.
 * A prop file is not a figure — `props.glb` is five unrelated accessories
 * sharing one fetch — so taking sixteen pawns' worth of helmets out of it
 * should not clone the other four props each time.
 *
 * The clone shares geometry, exactly as `rig()`'s does, which puts it under
 * the same rule: debris.js must never dispose one, and `disposeParts` frees it
 * once via the rig scene it came from. It also arrives with no material worth
 * having, since the file is exported with `export_materials='NONE'` — the
 * caller is expected to paint it.
 */
export function prop(key, name) {
  const node = rigs.get(key)?.getObjectByName(name);
  if (!node) return null;
  const clone = node.clone(true);
  clone.traverse((o) => {
    if (o.isMesh) {
      o.castShadow = true;
      o.receiveShadow = true;
    }
  });
  return clone;
}

export function disposeParts() {
  for (const g of library.values()) g.dispose();
  for (const g of scaled.values()) g.dispose();
  for (const scene of rigs.values()) {
    scene.traverse((o) => { if (o.isMesh) o.geometry.dispose(); });
  }
  library.clear();
  scaled.clear();
  rigs.clear();
  ready = false;
}
